import { LuIcon, Menu, type MenuClickTriggerProps, type MenuRootProps, type MenuVariantProps } from "@overmap-ai/blocks"
import type { FormSubmissionComment, Stored } from "@overmap-ai/common"
import { selectCurrentUser } from "@overmap-ai/core"
import { memo, type ReactElement, useCallback } from "react"

import { modelIconMapping } from "../../mappings/icons"
import { useAppSelector } from "../../store"

interface FormSubmissionCommentDropdownMenuProps
	extends MenuRootProps, Omit<MenuClickTriggerProps, "children">, MenuVariantProps {
	trigger: ReactElement
	formSubmissionComment: Stored<FormSubmissionComment>
	onResolvedChange: (formSubmissionComment: Stored<FormSubmissionComment>, resolved: boolean) => void
	onEdit: () => void
	onDelete: (formSubmissionComment: Stored<FormSubmissionComment>) => void
}

export const FormSubmissionCommentDropdownMenu = memo((props: FormSubmissionCommentDropdownMenuProps) => {
	const {
		trigger,
		disabled,
		formSubmissionComment,
		onResolvedChange,
		onEdit,
		onDelete,
		size,
		radius,
		variant,
		accentColor,
	} = props
	const currentUser = useAppSelector(selectCurrentUser)

	const handleResolvedChange = useCallback(() => {
		onResolvedChange(formSubmissionComment, !formSubmissionComment.resolved)
	}, [formSubmissionComment, onResolvedChange])

	const handleDelete = useCallback(() => {
		onDelete(formSubmissionComment)
	}, [formSubmissionComment, onDelete])

	return (
		<Menu.Root>
			<Menu.ClickTrigger disabled={disabled}>{trigger}</Menu.ClickTrigger>
			<Menu.Content size={size} radius={radius} accentColor={accentColor} variant={variant}>
				<Menu.Group>
					<Menu.Item onSelect={handleResolvedChange}>
						<LuIcon
							icon={formSubmissionComment.resolved ? modelIconMapping.refresh : modelIconMapping.check}
						/>
						{formSubmissionComment.resolved ? "Unresolve" : "Resolve"}
					</Menu.Item>
					{currentUser?.id === formSubmissionComment.created_by && (
						<>
							<Menu.Item onSelect={onEdit}>
								<LuIcon icon={modelIconMapping.edit} />
								Edit
							</Menu.Item>
							<Menu.Item onSelect={handleDelete}>
								<LuIcon icon={modelIconMapping.delete} />
								Delete
							</Menu.Item>
						</>
					)}
				</Menu.Group>
			</Menu.Content>
		</Menu.Root>
	)
})
FormSubmissionCommentDropdownMenu.displayName = "FormSubmissionCommentDropdownMenu"
